import React, { useEffect, useState } from "react";
import { ArrowBack } from "@mui/icons-material";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import StatusChip from "../components/StatusChip";
import { AppButton } from "components/common/buttons";
import { subcontractApi } from "../services/subcontractApi";
import AcceptancesTab from "../tabs/AcceptancesTab";
import ApprovalsTab from "../tabs/ApprovalsTab";
import PaymentsTab from "../tabs/PaymentsTab";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";
import { useAuth } from "context/AuthContext";
import api from "services/api";
import { isAdminUser } from "modules/systems/services/application.service";

const configs = {
  payments: {
    label: "Thanh toan",
    listPath: "/subcontracts/payments",
    readPermission: SUBCONTRACT_PERMISSIONS.paymentRead,
    load: async (id) => {
      const [records, items, sections, subcontractApprovalProfiles] = await Promise.all([
        subcontractApi.getPaymentProfiles(id),
        subcontractApi.getItems(id),
        subcontractApi.getSections(id),
        subcontractApi.getApprovalProfilesBySubcontract(id),
      ]);
      return { records, items, sections, subcontractApprovalProfiles };
    },
  },
  acceptances: {
    label: "Nghiệm thu",
    listPath: "/subcontracts/acceptances",
    canRead: (permissions) => permissions.canReadAcceptance,
    load: async (id) => {
      const [records, items, sections, subcontractApprovalProfiles] = await Promise.all([
        subcontractApi.getAcceptanceProfiles(id),
        subcontractApi.getItems(id),
        subcontractApi.getSections(id),
        subcontractApi.getApprovalProfilesBySubcontract(id),
      ]);
      return { records, items, sections, subcontractApprovalProfiles };
    },
  },
  "acceptance-approvals": {
    label: "Phê duyệt nghiệm thu",
    listPath: "/subcontracts/acceptance-approvals",
    readPermission: SUBCONTRACT_PERMISSIONS.acceptanceApprovalRead,
    load: async (id) => {
      const [records, items, sections, subcontractApprovalProfiles] = await Promise.all([
        subcontractApi.getAcceptanceProfiles(id),
        subcontractApi.getItems(id),
        subcontractApi.getSections(id),
        subcontractApi.getApprovalProfilesBySubcontract(id),
      ]);
      return { records, items, sections, subcontractApprovalProfiles };
    },
  },
  approvals: {
    label: "Phê duyệt",
    listPath: "/subcontracts/approvals",
    readPermission: SUBCONTRACT_PERMISSIONS.approvalRead,
    load: async (id) => {
      const [records, items, draftItems, sections] = await Promise.all([
        subcontractApi.getApprovalProfilesBySubcontract(id),
        subcontractApi.getItems(id),
        subcontractApi.getDraftItems(id),
        subcontractApi.getSections(id),
      ]);
      return { records, items, draftItems, sections };
    },
  },
};

export default function SubcontractWorkflowDetailPage({ type }) {
  const config = configs[type];
  const permissions = useSubcontractPermissions();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [records, setRecords] = useState([]);
  const [items, setItems] = useState([]);
  const [draftItems, setDraftItems] = useState([]);
  const [sections, setSections] = useState([]);
  const [subcontractApprovalProfiles, setSubcontractApprovalProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hasAssignedAccess, setHasAssignedAccess] = useState(null);

  const canRead = config.canRead ? config.canRead(permissions) : permissions.has(config.readPermission);

  useEffect(() => {
    let active = true;

    const checkAccess = async () => {
      if (!user?.userId) {
        if (active) setHasAssignedAccess(false);
        return;
      }

      if (isAdminUser(user)) {
        if (active) setHasAssignedAccess(true);
        return;
      }

      try {
        const response = await api.get(`/UserSubcontract/user/${user.userId}`, {
          params: { pageSize: 1000, pageNumber: 1 },
        });
        const entries = response?.data?.data || [];
        const allowed = entries.some(
          (item) => Number(item.subcontractId ?? item.SubcontractId) === Number(id),
        );

        if (active) setHasAssignedAccess(allowed);
      } catch (requestError) {
        if (active) {
          setHasAssignedAccess(false);
          setError(
            requestError.response?.data?.message ||
            requestError.message ||
            "Không thể kiểm tra quyền truy cập giao khoán.",
          );
        }
      }
    };

    setHasAssignedAccess(null);
    checkAccess();

    return () => {
      active = false;
    };
  }, [id, user]);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (permissions.loading || !canRead || hasAssignedAccess !== true) return;
      setLoading(true);
      setError("");
      try {
        const [contractData, workflowData] = await Promise.all([
          subcontractApi.getSubcontract(id),
          config.load(id),
        ]);
        if (active) {
          setContract(contractData);
          setRecords(workflowData.records || []);
          setItems(workflowData.items || []);
          setDraftItems(workflowData.draftItems || []);
          setSections(workflowData.sections || []);
          setSubcontractApprovalProfiles(workflowData.subcontractApprovalProfiles || []);
        }
      } catch (requestError) {
        if (active) {
          setContract(null);
          setRecords([]);
          setItems([]);
          setSections([]);
          setSubcontractApprovalProfiles([]);
          setError(requestError.response?.data?.message || requestError.message || `Không thể tải chi tiết ${config.label}.`);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (hasAssignedAccess === false) {
      setLoading(false);
      setContract(null);
      setRecords([]);
      setItems([]);
      setSections([]);
      setSubcontractApprovalProfiles([]);
      if (!error) {
        setError("Bạn không được phân quyền xem quyết định giao khoán này.");
      }
      return () => {
        active = false;
      };
    }

    load();
    return () => {
      active = false;
    };
  }, [canRead, config, error, hasAssignedAccess, id, permissions.loading]);

  if (!permissions.loading && !canRead) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (loading || permissions.loading || hasAssignedAccess === null) {
    return (
      <Box className="subcontract-page subcontract-workflow-detail-loading">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box className="subcontract-page">
        <Alert severity="error">{error || "Không tìm thấy quyết định giao khoán."}</Alert>
      </Box>
    );
  }

  return (
    <Box className="subcontract-page">
      <Box className="subcontract-workflow-detail-hero">
        <AppButton className="subcontract-workflow-detail-hero__back" color="inherit" onClick={() => navigate(config.listPath)}>
          <ArrowBack />
        </AppButton>
        <Box className="subcontract-workflow-detail-hero__content">
          <Typography className="subcontract-workflow-detail-hero__eyebrow">Chi tiết {config.label.toLowerCase()}</Typography>
          <Typography className="subcontract-workflow-detail-hero__title">
            {contract.subcontractNumber} - {contract.subcontractName}
          </Typography>
        </Box>
        <Box className="subcontract-workflow-detail-hero__status">
          <StatusChip value={contract.status} />
          {/* {type === "approvals" && <StatusChip value={contract.approvalStatus || "Tạo mới"} />} */}
        </Box>
      </Box>

      <Box className="subcontract-detail-content">
        {type === "payments" && <PaymentsTab subcontractId={id} subcontract={contract} payments={records} items={items} sections={sections} subcontractApprovalProfiles={subcontractApprovalProfiles} onChanged={() => window.location.reload()} />}
        {type === "acceptances" && <AcceptancesTab subcontractId={id} subcontract={contract} acceptances={records} items={items} sections={sections} subcontractApprovalProfiles={subcontractApprovalProfiles} onChanged={() => window.location.reload()} />}
        {type === "acceptance-approvals" && <AcceptancesTab viewMode="approval" subcontractId={id} subcontract={contract} acceptances={records} items={items} sections={sections} subcontractApprovalProfiles={subcontractApprovalProfiles} onChanged={() => window.location.reload()} />}
        {type === "approvals" && <ApprovalsTab subcontractId={id} subcontract={contract} approvals={records} items={draftItems} officialItems={items} sections={sections} onChanged={() => window.location.reload()} />}
      </Box>
    </Box>
  );
}
